/**
 * Team challenges (P3.5) — the workspace's challenges, and the create form for
 * callers Central grants training:write. Hidden entirely while the server has
 * team challenges switched off.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useGetCapabilitiesQuery } from '../store/api/capabilitiesApi';
import { useListChallengesQuery } from '../store/api/challengesApi';
import { useCentralConsumer } from '../hooks/useCentralConsumer';
import { CreateChallengeForm } from '../components/challenges/CreateChallengeForm';
import { describeCondition, MODE_LABEL } from '../components/challenges/challengeTypes';
import { LoadErrorNotice } from '../components/LoadErrorNotice';

const STATUS_LABEL = { upcoming: 'Upcoming', active: 'Active', ended: 'Ended', archived: 'Archived' } as const;

export function ChallengesPage() {
  const navigate = useNavigate();
  const {
    data: serverCaps,
    isLoading: capsLoading,
    isError: capsFailed,
    error: capsError,
    refetch: refetchCaps,
  } = useGetCapabilitiesQuery();
  const enabled = serverCaps?.teamChallenges === true;
  const { hasCapability } = useCentralConsumer();
  // Same capability the server requires to create (training assignments use it too).
  const canCreate = enabled && hasCapability('training', 'write');
  const {
    data: challenges,
    isLoading,
    isError: listFailed,
    error: listError,
    refetch: refetchList,
  } = useListChallengesQuery(undefined, { skip: !enabled });
  const [showForm, setShowForm] = useState(false);

  if (capsLoading) return <p className="text-gray-500">Loading…</p>;
  // Not knowing whether challenges are on is not the same as them being off.
  if (capsFailed) {
    return (
      <div className="card p-6">
        <h1 className="text-xl font-semibold">Team challenges</h1>
        <div className="mt-2">
          <LoadErrorNotice
            error={capsError}
            onRetry={() => void refetchCaps()}
            failedMessage="Team challenges could not be loaded."
            testIdPrefix="challenges"
          />
        </div>
      </div>
    );
  }
  if (!enabled) {
    return (
      <div className="card p-6" data-testid="challenges-off">
        <h1 className="text-xl font-semibold">Team challenges</h1>
        <p className="mt-2 text-gray-500">Team challenges are not available yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team challenges</h1>
          <p className="mt-1 text-gray-500">Shared goals for your workspace, measured from your completed sessions.</p>
        </div>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Close' : 'New challenge'}
          </button>
        )}
      </div>

      {canCreate && showForm && (
        <div className="card p-4">
          <CreateChallengeForm onCreated={(id) => navigate(`/challenges/${id}`)} />
        </div>
      )}

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : listFailed ? (
        <LoadErrorNotice
          error={listError}
          onRetry={() => void refetchList()}
          failedMessage="Your workspace's challenges could not be loaded."
          testIdPrefix="challenges"
        />
      ) : !challenges?.length ? (
        <div data-testid="challenges-empty">
          <p className="text-gray-500">No challenges in this workspace yet.</p>
          <p className="mt-1 text-sm text-gray-500">
            {canCreate
              ? 'Use “New challenge” to set a shared goal for your workspace.'
              : 'Challenges appear here when someone who manages training in your workspace creates one.'}
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {challenges.map((c) => (
            <li key={c.id} className="card p-4">
              <Link to={`/challenges/${c.id}`} className="font-semibold hover:underline">
                {c.title}
              </Link>
              <p className="text-sm text-gray-600">{describeCondition(c.condition)}</p>
              <p className="text-xs text-gray-500">
                {STATUS_LABEL[c.status]} · {MODE_LABEL[c.mode] ?? 'Personal goal'} · {new Date(c.startsAt).toLocaleDateString()} – {new Date(c.endsAt).toLocaleDateString()} ·{' '}
                {c.participantCount} joined{c.joined ? ' · you joined' : ''}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default ChallengesPage;
